import { AxiosError } from 'axios'
import { useCallback } from 'react'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { PaymentGatewayInterface } from '@/interfaces/ServerInterfaces'

import APIServices from '../../api/APIServices'

export interface PaymentGatewaysQueryParams {
  businessId: number
  per_page?: number
  page?: number
  search?: string | null
  isActive?: boolean | null
}

// Create a unique, descriptive key for this query
export const PAYMENT_GATEWAYS_QUERY_KEY = 'payment-gateways'

export interface PaymentGatewaysResponse {
  items: PaymentGatewayInterface[]
  totalItems: number
  currentPage: number
  totalPages: number
}

/**
 * Fetch payment gateways from the API
 * Separating API call logic from the hook for better testability
 */
const fetchPaymentGateways = async (
  params?: PaymentGatewaysQueryParams
): Promise<PaymentGatewaysResponse> => {
  // Filter out null/undefined values to clean up the request
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value != null)
  )

  const response = await APIServices.get('/administration/paymentgateway', {
    params: cleanParams,
  })
  return response.data
}

/**
 * Custom hook for fetching payment gateways
 *
 * @param params - Query parameters including businessId and pagination
 * @param options - TanStack Query options for customizing query behavior
 * @param businessId - Business ID for scoping
 * @returns Query result object containing data, loading states, and utility functions
 */
export const usePaymentGatewaysQuery = ({
  params,
  options,
  businessId,
}: {
  params?: PaymentGatewaysQueryParams
  options?: Partial<UseQueryOptions<PaymentGatewaysResponse, AxiosError>>
  businessId: number
}) => {
  // Query hook
  const queryResult = useQuery<PaymentGatewaysResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: getPaymentGatewaysQueryKey({ businessId, params }),

    // Separate the API call logic from the query configuration
    queryFn: () => fetchPaymentGateways(params),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  // Filter only active payment gateways
  const activePaymentGateways =
    queryResult.data?.items.filter((gateway) => gateway.isActive) || []

  // Helper functions for pagination metadata
  const getCurrentPage = useCallback(() => {
    return params?.page || 1
  }, [params?.page])

  const getPageSize = useCallback(() => {
    return params?.per_page || 10
  }, [params?.per_page])

  const hasNextPage = useCallback(() => {
    if (!queryResult.data) return false
    const { currentPage, totalPages } = queryResult.data
    return currentPage < totalPages
  }, [queryResult.data])

  const hasPreviousPage = useCallback(() => {
    const currentPage = getCurrentPage()
    return currentPage > 1
  }, [getCurrentPage])

  return {
    ...queryResult,
    paymentGateways: activePaymentGateways,
    isLoadingPaymentGateways: queryResult.isLoading,
    pagination: {
      currentPage: getCurrentPage(),
      pageSize: getPageSize(),
      hasNextPage: hasNextPage(),
      hasPreviousPage: hasPreviousPage(),
      totalPages: queryResult.data?.totalPages || 0,
      totalItems: queryResult.data?.totalItems || 0,
      params: {
        page: 1,
        per_page: 20,
        ...params,
      },
    },
  }
}

/**
 * Helper function to get the payment gateways query key
 * Useful for cache invalidation or prefetching
 */
export const getPaymentGatewaysQueryKey = ({
  businessId,
  params,
}: {
  businessId: number
  params?: PaymentGatewaysQueryParams
}) => [PAYMENT_GATEWAYS_QUERY_KEY, { businessId, params }]
